import { useQueryClient } from "@tanstack/react-query";
import { Bell, User } from "lucide-react";
import { useMemo } from "react";

import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { InnerPageNavigation } from "@/features/page-layouts/inner-page-navigation";
import { queryKeyMap } from "@/features/query-client";

import { NotificationsSection } from "../components/notification-settings-section";
import { ProfileSection } from "../components/profile-section";
import { useGetMeQuery } from "../hooks/use-get-me.query";
import { useUpdateUserMutation } from "../hooks/use-update-user.mutation";
import { useUpdateUserCompanySettingsMutation } from "../hooks/use-update-user-company-settings.mutation";

export const MyProfilePage = () => {
  const { data: user, isError, refetch } = useGetMeQuery();
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutateAsync: updateUser } = useUpdateUserMutation();
  const { mutate: updateUserCompanySettings } =
    useUpdateUserCompanySettingsMutation();

  const userSettings = lenderCompany?.userSettings;
  const isAdmin = user?.isAdmin ?? false;

  const queryClient = useQueryClient();

  const navigationItems = useMemo(() => {
    const items = [
      {
        id: "personal-information",
        label: "Personal Information",
        icon: User,
      },
    ];

    if (!isAdmin) {
      items.push({
        id: "notifications",
        label: "Notifications",
        icon: Bell,
      });
    }

    return items;
  }, [isAdmin]);

  if (isError || !user) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-6 sm:p-8">
        <div className="rounded-xl border p-6">
          <p className="font-semibold">Couldn’t load user settings</p>
          <p className="mt-1 text-sm">Please try again.</p>
          <button
            className="cursor-pointer mt-4 rounded-lg px-4 py-2 text-white"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      </main>
    );
  }

  return (
    <div>
      <div className="relative mt-8 grid grid-cols-1 gap-10 md:grid-cols-12">
        <aside className="md:col-span-3 md:self-start">
          <InnerPageNavigation
            offsetTopPx={16}
            scrollContainerSelector="#main-content-scroll-container"
            items={navigationItems}
          />
        </aside>

        <div className="space-y-10 md:col-span-9">
          <section id="personal-information" className="scroll-mt-24">
            <ProfileSection
              user={user}
              onUpdateUser={async (data) => {
                if (!lenderCompany?.id) return;
                await updateUser(
                  {
                    lenderCompanyId: lenderCompany.id,
                    id: user.id,
                    variables: data,
                  },
                  {
                    onSuccess: (updated) => {
                      queryClient.setQueryData(queryKeyMap.getMe, updated);
                    },
                  },
                );
              }}
            />
          </section>

          {!isAdmin && userSettings && (
            <section id="notifications" className="scroll-mt-24">
              <NotificationsSection
                settings={userSettings}
                onUpdateUserSettings={(data) =>
                  updateUserCompanySettings(
                    {
                      lenderCompanyId: lenderCompany.id,
                      userId: user.id,
                      data,
                    },
                    {
                      onSuccess: () => {
                        queryClient.invalidateQueries({
                          queryKey: queryKeyMap.getLenderCompany(
                            lenderCompany.id,
                          ),
                        });
                      },
                    },
                  )
                }
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
